// TRAINING (sandbox) mode (owner 2026-11-02: "a mode to test out all the fusions… or a training mode to test out all the
// skills"). Pure and deterministic like the rest of src/core: no DOM, no three, no Math.random — the only randomness is the
// Sim's own seeded Rng. Everything here exists only when SimOptions.training is set; a normal run never constructs it, and
// the Sim reads tweak values through `sim.tw`, which is a frozen copy of DEFAULT_TWEAKS outside training (x * 1 === x).
//
// What lives here: the tweak values (TWEAKS tab), training dummies (immortal targets with damage readouts), the in-world
// ARMORY (pedestals near the spawn that equip / drop skills, traits and fusions), the loadout API (set rank, evolve, fuse
// now, drop, reset), enemy / boss spawning helpers, the DPS meter and the 20 s benchmark, build presets (pure
// serialisation; the app keeps them in localStorage), hazard triggers and live run-modifier toggles.
import { ENEMIES, EnemyDef, EnemyKind, INNATE, MAX_PASSIVE_LEVEL, MAX_PASSIVES, MAX_WEAPON_LEVEL, MAX_WEAPONS, PASSIVES, PassiveId, WEAPONS, WeaponId } from './content';
import { FUSIONS, FUSION_IDS, isFusionId, type FusionId } from './fusions';
import { DT, PASSIVE_K, SURGE_STAGE_END, SURGE_TIERS } from './skillStats';
import { HZ } from './hazards';
import { HURRY_K, MOD_CONFLICTS, ModId, PACE, PACE_HYPER, RELAXED_DMG, RELAXED_HP, TOUGH_DMG, TOUGH_HP } from './mods';
import type { Enemy, Sim } from './sim';

// ---------------------------------------------------------------------------------------------------------------------
// Tweaks
// ---------------------------------------------------------------------------------------------------------------------
/** Live multipliers / switches (TWEAKS tab). Defaults equal the normal game exactly. */
export interface Tweaks {
  /** player damage, cooldown, area, projectile speed, move speed, pickup radius multipliers */
  dmg: number; cd: number; area: number; proj: number; move: number; magnet: number;
  /** crit chance override 0..1 (-1 = the build's own) */
  crit: number;
  /** extra HP regen per second */
  regen: number;
  /** Surge charge rate multiplier; XP gain multiplier */
  ki: number; xp: number;
  /** enemy health (new spawns; changing it rescales the living), enemy hit damage, enemy speed, director spawn rate */
  ehp: number; edmg: number; espd: number; spawn: number;
  /** cap on enemies on the field (0 = the quality cap) */
  maxE: number;
  /** chance a director spawn is an elite (0 = the normal rules only) */
  elite: number;
  /** enemies attack (contact, slams, shots); off = passive foes to watch */
  attack: boolean;
  /** knockback multiplier */
  knock: number;
  /** every hit on a non-dummy foe is lethal */
  ohk: boolean;
  /** unlimited rerolls and bans */
  freeDraft: boolean;
  /** Surge bar always full */
  infSurge: boolean;
  /** owner 2026-11-03 'unlimited surge option in training': an active Surge never runs out (the timer is held full) */
  holdSurge: boolean;
  /** owner 2026-11-03 'I need a disable level up setting': XP still fills the bar but never levels up (the LEVEL UP buttons still work) */
  noLevel: boolean;
}
export const DEFAULT_TWEAKS: Readonly<Tweaks> = Object.freeze({
  dmg: 1, cd: 1, area: 1, proj: 1, move: 1, magnet: 1, crit: -1, regen: 0, ki: 1, xp: 1,
  ehp: 1, edmg: 1, espd: 1, spawn: 1, maxE: 0, elite: 0, attack: true, knock: 1, ohk: false, freeDraft: false, infSurge: false, holdSurge: false, noLevel: false,
});
/** slider steps for each numeric tweak (the panel walks these; values off-grid are snapped to the nearest) */
export const TWEAK_STEPS: Record<string, readonly number[]> = {
  dmg: [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 5, 10],
  cd: [0.05, 0.1, 0.25, 0.5, 0.75, 1, 1.5, 2],
  area: [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3],
  proj: [0.5, 0.75, 1, 1.5, 2, 3],
  move: [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3],
  magnet: [0.5, 1, 1.5, 2, 3, 5, 10],
  crit: [-1, 0, 0.1, 0.25, 0.5, 0.75, 1],
  regen: [0, 1, 2, 5, 10, 25, 50],
  ki: [0, 0.5, 1, 2, 3, 5, 10],
  xp: [0, 0.5, 1, 2, 3, 5, 10],
  ehp: [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 5, 10],
  edmg: [0, 0.25, 0.5, 1, 1.5, 2, 3, 5],
  espd: [0, 0.25, 0.5, 0.75, 1, 1.5, 2, 3],
  spawn: [0, 0.25, 0.5, 1, 1.5, 2, 3, 5],
  maxE: [0, 25, 50, 100, 150, 200, 300, 400, 600, 800, 1000],
  elite: [0, 0.02, 0.05, 0.1, 0.25, 0.5, 1],
  knock: [0, 0.25, 0.5, 1, 1.5, 2, 3],
};
export function stepTweak(k: keyof Tweaks, v: number, dir: number): number {
  const s = TWEAK_STEPS[k]; if (!s) return v;
  let i = 0, bd = Infinity; s.forEach((q, j) => { const d = Math.abs(q - v); if (d < bd) { bd = d; i = j; } });
  return s[Math.max(0, Math.min(s.length - 1, i + dir))];
}

// ---------------------------------------------------------------------------------------------------------------------
// Dummies
// ---------------------------------------------------------------------------------------------------------------------
export type DummyType = 'std' | 'elite' | 'armor' | 'boss';
export type DummyMove = 'still' | 'circle' | 'strafe';
/** dummy health: effectively endless; refilled whenever it drops below DUMMY_REFILL of max */
export const DUMMY_HP = 1e6, DUMMY_BOSS_HP = 60000, DUMMY_REFILL = 0.1;
/** damage readout window (s) and bucket length (s) */
export const DUMMY_WINDOW = 5, DUMMY_BUCKET = 0.25;
const NB = Math.round(DUMMY_WINDOW / DUMMY_BUCKET);
/** dummy body defs: real kinds so the real rules apply (elite flag, Bulwark plating, boss forms / gates); never attack */
export const DUMMY_DEFS: Record<DummyType, EnemyDef> = {
  std: { kind: 'drone', ki: 0, hp: DUMMY_HP, speed: 0, radius: 0.75, damage: 0, xp: 0, mass: 1e4 },
  elite: { kind: 'drone', ki: 0, hp: DUMMY_HP, speed: 0, radius: 0.75, damage: 0, xp: 0, mass: 1e4 },
  armor: { kind: 'warden', ki: 0, hp: DUMMY_HP, speed: 0, radius: 1.0, damage: 0, xp: 0, mass: 1e4 },
  boss: { kind: 'boss', ki: 0, hp: DUMMY_BOSS_HP, speed: 0, radius: 2.4, damage: 0, xp: 0, mass: 1e4 },
};
export const DUMMY_TYPE_CODE: Record<DummyType, number> = { std: 1, elite: 2, armor: 3, boss: 4 };
export const DUMMY_TYPES: readonly DummyType[] = ['std', 'elite', 'armor', 'boss'];
export interface DummyInfo {
  id: number; type: DummyType; move: DummyMove; speed: number;
  /** anchor (circle centre / strafe midpoint / rest spot) */
  ax: number; az: number; ph: number;
  /** damage per DUMMY_BUCKET slot over the last DUMMY_WINDOW s (ring), hits in the window, totals since placed */
  buckets: number[]; hitB: number[]; total: number; hits: number; last: number;
}
export type DummyLayout = 'one' | 'line5' | 'ring8' | 'cluster20';
/** moving-dummy speeds (m/s) */
export const DUMMY_SLOW = 2, DUMMY_FAST = 6, DUMMY_PATH_R = 4;

// ---------------------------------------------------------------------------------------------------------------------
// Armory (in-world pedestals)
// ---------------------------------------------------------------------------------------------------------------------
export type PedKind = 'skill' | 'trait' | 'fusion';
export interface Pedestal { i: number; kind: PedKind; id: string; x: number; z: number }
/** how close (m) the hero must stand to a pedestal for its prompt */
export const ARMORY_R = 1.35;
export const ARMORY_GAP = 3.2;
export const ARMORY_SKILLS = (): WeaponId[] => (Object.keys(WEAPONS) as WeaponId[]).filter((id) => !WEAPONS[id].retired && !INNATE.includes(id));
export const ARMORY_TRAITS = (): PassiveId[] => Object.keys(PASSIVES) as PassiveId[];
/** the dummy yard: in front of the spawn (towards the camera) */
export const YARD = { x: 0, z: 10 };
/** Pedestal layout near the spawn (0, 0): skills in a block to the north (up the screen), traits to the west, fusions to
 *  the east. Deterministic (tests use it). */
export function armoryLayout(): Pedestal[] {
  const out: Pedestal[] = []; const S = ARMORY_GAP;
  const block = (kind: PedKind, ids: string[], cols: number, x0: number, z0: number, dirX: number) => {
    ids.forEach((id, k) => { const c = k % cols, r = Math.floor(k / cols); out.push({ i: out.length, kind, id, x: x0 + dirX * c * S, z: z0 - r * S }); });
  };
  const sk = ARMORY_SKILLS(), cols = 10;
  block('skill', sk, cols, -((cols - 1) * S) / 2, -6, 1);
  block('trait', ARMORY_TRAITS(), 4, -19, -2, -1);
  block('fusion', [...FUSION_IDS], 4, 19, -2, 1);
  return out;
}
/** bounds the armory + yard keep clear of props (x0, z0, x1, z1) */
export function armoryBounds(peds: readonly Pedestal[]): [number, number, number, number] {
  let x0 = YARD.x - 13, x1 = YARD.x + 13, z0 = -4, z1 = YARD.z + 9;
  for (const p of peds) { x0 = Math.min(x0, p.x - 2.5); x1 = Math.max(x1, p.x + 2.5); z0 = Math.min(z0, p.z - 2.5); z1 = Math.max(z1, p.z + 2.5); }
  return [x0, z0, x1, z1];
}
/** nearest pedestal within ARMORY_R of (x, z), else -1 */
export function nearestPedestal(peds: readonly Pedestal[], x: number, z: number, r = ARMORY_R): number {
  let best = -1, bd = r * r;
  for (const p of peds) { const d = (p.x - x) ** 2 + (p.z - z) ** 2; if (d <= bd) { bd = d; best = p.i; } }
  return best;
}

// ---------------------------------------------------------------------------------------------------------------------
// Benchmark + presets (pure)
// ---------------------------------------------------------------------------------------------------------------------
export interface BenchRow { src: string; dmg: number; dps: number; hits: number; share: number }
export interface BenchResult { secs: number; total: number; dps: number; rows: BenchRow[] }
/** damage / hits deltas between two snapshots over `secs` seconds, biggest first */
export function benchTable(d0: Record<string, number>, d1: Record<string, number>, h0: Record<string, number>, h1: Record<string, number>, secs: number): BenchResult {
  const keys = new Set([...Object.keys(d1), ...Object.keys(h1)]);
  const rows: BenchRow[] = []; let total = 0;
  for (const k of keys) {
    const dmg = (d1[k] ?? 0) - (d0[k] ?? 0), hits = (h1[k] ?? 0) - (h0[k] ?? 0);
    if (dmg <= 0 && hits <= 0) continue;
    rows.push({ src: k, dmg, dps: secs > 0 ? dmg / secs : 0, hits, share: 0 }); total += Math.max(0, dmg);
  }
  for (const r of rows) r.share = total > 0 ? Math.max(0, r.dmg) / total : 0;
  rows.sort((a, b) => b.dmg - a.dmg || (a.src < b.src ? -1 : 1));
  return { secs, total, dps: secs > 0 ? total / secs : 0, rows };
}
/** delta of a result row vs a previous result (dps difference and ratio; null when the skill was not in the old one) */
export function benchDelta(cur: BenchRow, prev: BenchResult | null): { d: number; k: number } | null {
  const o = prev?.rows.find((r) => r.src === cur.src); if (!o) return null;
  return { d: cur.dps - o.dps, k: o.dps > 0 ? cur.dps / o.dps : 0 };
}
export interface BuildPreset { name: string; weapons: [WeaponId, number][]; evolved: WeaponId[]; passives: [PassiveId, number][]; fusions: FusionId[] }
export const BENCH_SECS = 20;
/** the build of a sim as a preset */
export function snapshotBuild(sim: Sim, name: string): BuildPreset {
  return { name, weapons: [...sim.weapons], evolved: [...sim.evolved], passives: [...sim.passives].filter(([, l]) => l > 0), fusions: [...sim.fusion.owned.keys()] };
}
/** parse stored presets (anything malformed is dropped) */
export function parsePresets(raw: unknown): BuildPreset[] {
  if (!Array.isArray(raw)) return [];
  const out: BuildPreset[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const pairs = <T extends string>(a: unknown, ok: (s: string) => s is T, max: number): [T, number][] =>
      Array.isArray(a) ? a.filter((q): q is [T, number] => Array.isArray(q) && typeof q[0] === 'string' && ok(q[0]) && typeof q[1] === 'number').map(([k, v]) => [k, Math.max(1, Math.min(max, Math.round(v)))]) : [];
    const isW = (s: string): s is WeaponId => Object.prototype.hasOwnProperty.call(WEAPONS, s);
    const isP = (s: string): s is PassiveId => Object.prototype.hasOwnProperty.call(PASSIVES, s);
    out.push({
      name: typeof o.name === 'string' ? o.name.slice(0, 32) : 'Preset',
      weapons: pairs(o.weapons, isW, MAX_WEAPON_LEVEL), passives: pairs(o.passives, isP, 10),
      evolved: Array.isArray(o.evolved) ? o.evolved.filter((s): s is WeaponId => typeof s === 'string' && isW(s)) : [],
      fusions: Array.isArray(o.fusions) ? o.fusions.filter((s): s is FusionId => typeof s === 'string' && isFusionId(s)) : [],
    });
  }
  return out;
}
/** a few built-in example builds */
export const BUILTIN_PRESETS: readonly BuildPreset[] = [
  { name: 'Starter kit', weapons: [['bolt', 1], ['surge', 1]], evolved: [], passives: [], fusions: [] },
  { name: 'Beam & blast (max)', weapons: [['surge', 5], ['lance', 5], ['nova', 5], ['mortar', 5], ['zenith', 5]], evolved: ['lance', 'nova'], passives: [['power', 5], ['focus', 5], ['haste', 5]], fusions: [] },
  { name: 'Projectile swarm', weapons: [['surge', 3], ['bolt', 5], ['disc', 5], ['barrage', 5], ['finger', 5], ['comet', 5]], evolved: ['bolt', 'disc'], passives: [['echo', 3], ['velocity', 5], ['keen', 5]], fusions: [] },
  // group C traits (owner 2026-11-03): crowd control + the lance signature + the one-time save
  { name: 'Lance & control', weapons: [['surge', 3], ['lance', 5], ['nova', 5], ['shout', 5], ['mortar', 5]], evolved: ['lance'], passives: [['lancecraft', 5], ['heavyhands', 5], ['secondwind', 2], ['haste', 3]], fusions: [] },
  { name: 'Fire family', weapons: [['surge', 3], ['breath', 5], ['flamering', 5], ['arc', 4]], evolved: [], passives: [['kindling', 3], ['emberheart', 3], ['power', 3]], fusions: [] },
  // group A core stat traits (owner 2026-11-03): crit damage on Keen Edge, extra pierce, lifesteal and Momentum
  { name: 'Crit & pierce', weapons: [['surge', 3], ['bolt', 5], ['finger', 4], ['crescent', 5]], evolved: [], passives: [['keen', 5], ['critdmg', 5], ['lancet', 5], ['lifedraw', 3], ['momentum', 5]], fusions: [] },
  // Group D (owner 2026-11-03): the economy and risk traits on a mid build, to feel Overload's tougher foes
  { name: 'Fortune & Overload', weapons: [['surge', 3], ['bolt', 5], ['nova', 4], ['arc', 4]], evolved: [], passives: [['fortune', 5], ['greed', 5], ['curse', 3], ['power', 3]], fusions: [] },
  // elemental families (owner 2026-11-03): frost around the freezing skills, storm around the lightning ones
  { name: 'Frost traits', weapons: [['surge', 3], ['glare', 5], ['shout', 5], ['bolt', 5], ['finger', 5]], evolved: ['glare'], passives: [['deepchill', 3], ['frostbite', 3], ['focus', 3]], fusions: [] },
  { name: 'Storm family', weapons: [['surge', 3], ['arc', 5], ['barrage', 5], ['skyfall', 5], ['orbit', 5]], evolved: ['arc'], passives: [['static', 3], ['conductor', 3], ['power', 3]], fusions: [] },
  // ICE FAMILY (owner 2026-11-03): the four ice skills with the frost traits that amplify them
  { name: 'Ice family', weapons: [['surge', 3], ['rime', 5], ['spikes', 5], ['glacier', 5], ['blizzard', 5]], evolved: ['rime', 'blizzard'], passives: [['deepchill', 3], ['frostbite', 3], ['focus', 3]], fusions: [] },
];

// ---------------------------------------------------------------------------------------------------------------------
// Options + the Training state object
// ---------------------------------------------------------------------------------------------------------------------
export interface TrainingOptions {
  /** god mode at the start (default true) */
  god?: boolean;
  /** natural spawning paused at the start (default true) */
  directorPaused?: boolean;
  /** place the default line of 5 dummies (default true) */
  dummies?: boolean;
  /** build the in-world armory (and clear props from its ground; default true) */
  armory?: boolean;
}
/** time-of-day overrides (clock 0..1, 0.5 = noon); null = the map's own clock */
export const TOD_PICKS: readonly { id: string; label: string; clock: number | null }[] = [
  { id: 'auto', label: 'Map / run', clock: null }, { id: 'dawn', label: 'Dawn', clock: 0.27 }, { id: 'day', label: 'Noon', clock: 0.5 },
  { id: 'dusk', label: 'Dusk', clock: 0.76 }, { id: 'night', label: 'Midnight', clock: 0 },
];
/** live-toggle support per run modifier (UI shows the note) */
export const MOD_LIVE: Record<ModId, string> = {
  hyper: 'live', swarm: 'live', tough: 'live (new + living foes)', hurry: 'live (re-times the run clock)', noheal: 'live', elites: 'live', night: 'live',
  relaxed: 'live (new + living foes; XP and drops too)',
};
/** DPS meter: snapshot every SNAP_TICKS, window METER_SECS */
const SNAP_TICKS = 30, METER_SECS = 10;

export class Training {
  readonly tw: Tweaks = { ...DEFAULT_TWEAKS };
  directorPaused: boolean;
  /** enemies stand still and never attack (training-dummy mode for real foes) */
  frozen = false;
  /** skills don't auto-cast; they fire only through triggerNow (tapped HUD icons / panel) */
  manualFire = false;
  /** allow more than MAX_WEAPONS skills / MAX_PASSIVES traits */
  noSlotLimit = false;
  /** time-of-day override (0..1) or null */
  clockOverride: number | null = null;
  /** seconds until a timed boss spawn (-1 = none) */
  bossTimer = -1;
  /** per-skill trigger windows (s): while > 0 the skill may fire under Manual fire */
  readonly fireT: Record<string, number> = {};
  private beamReq = false; private surgeReq = false;
  readonly dummies = new Map<number, DummyInfo>();
  readonly hitsBySrc: Record<string, number> = {};
  /** DPS meter snapshots (dmgBySrc copies), newest last */
  private snaps: { t: number; d: Record<string, number> }[] = [];
  /** benchmark in progress / last two results */
  bench: { t: number; d0: Record<string, number>; h0: Record<string, number> } | null = null;
  benchResult: BenchResult | null = null; benchPrev: BenchResult | null = null;
  /** armory pedestals and the one the hero stands at (-1 = none) */
  readonly pedestals: Pedestal[];
  near = -1;
  /** the starting kit (RESET LOADOUT) */
  private readonly kit: BuildPreset;
  private readonly baseMaxHp: number;
  /** a swap just happened (render: a short flash on the hero) */
  swapT = 0;

  constructor(private readonly sim: Sim, o: TrainingOptions) {
    this.directorPaused = o.directorPaused ?? true;
    (sim.opts as { godMode: boolean }).godMode = o.god ?? true;
    this.kit = snapshotBuild(sim, 'kit');
    this.baseMaxHp = sim.player.maxHp;
    this.pedestals = o.armory === false ? [] : armoryLayout();
    if (this.pedestals.length) { // keep the armory + yard ground clear (dead props are not drawn and never collide)
      const [x0, z0, x1, z1] = armoryBounds(this.pedestals);
      for (const p of sim.terrain.props) if (p.x + p.r > x0 && p.x - p.r < x1 && p.z + p.r > z0 && p.z - p.r < z1) p.alive = false;
    }
    if (o.dummies ?? true) this.placeDummies('line5');
  }

  get god(): boolean { return this.sim.opts.godMode; }
  setGod(on: boolean): void { (this.sim.opts as { godMode: boolean }).godMode = on; }

  // ------------------------------------------------------------------ per tick (Sim.step calls this at the end)
  tick(): void {
    const s = this.sim, p = s.player;
    for (const k in this.fireT) if (this.fireT[k] > 0) this.fireT[k] = Math.max(0, this.fireT[k] - DT);
    if (this.swapT > 0) this.swapT = Math.max(0, this.swapT - DT);
    if (this.tw.infSurge && s.wlv('surge') > 0 && p.transformT <= 0) { s.surgeCdT = 0; p.ki = s.kiCap; }
    if (this.tw.holdSurge && p.transformT > 0) p.transformT = Math.max(p.transformT, s.surgeDuration); // UNLIMITED: the form never ends
    if (this.tw.freeDraft) { s.rerolls = Math.max(s.rerolls, 99); s.bans = Math.max(s.bans, 99); }
    if (this.bossTimer > 0) { this.bossTimer -= DT; if (this.bossTimer <= 0) { this.bossTimer = -1; this.spawnBoss('final', 1); } }
    // DPS meter + dummy readouts
    if (s.tick % SNAP_TICKS === 0) { this.snaps.push({ t: s.time, d: { ...s.dmgBySrc } }); while (this.snaps.length > METER_SECS * 60 / SNAP_TICKS + 1) this.snaps.shift(); }
    if (s.tick % Math.round(DUMMY_BUCKET / DT) === 0) for (const d of this.dummies.values()) { d.buckets.unshift(0); d.buckets.length = NB; d.hitB.unshift(0); d.hitB.length = NB; }
    if (this.bench && s.time - this.bench.t >= BENCH_SECS - 1e-9) this.finishBench();
    this.near = this.pedestals.length ? nearestPedestal(this.pedestals, p.x, p.z) : -1;
  }

  /** Sim.damageEnemy hook: per-source hit counts, dummy readouts */
  onHit(e: Enemy, d: number, src: string): void {
    this.hitsBySrc[src] = (this.hitsBySrc[src] ?? 0) + 1;
    if (!e.dummy) return;
    const di = this.dummies.get(e.id); if (!di) return;
    di.buckets[0] += d; di.hitB[0]++; di.total += d; di.hits++; di.last = d;
  }

  // ------------------------------------------------------------------ manual fire / trigger now
  /** Sim reads: may skill / fusion `id` cast this tick? (always true unless Manual fire is on and it wasn't triggered) */
  mayFire(id: string): boolean { return !this.manualFire || (this.fireT[id] ?? 0) > 0; }
  takeBeam(): boolean { const b = this.beamReq; this.beamReq = false; return b; }
  takeSurge(): boolean { const b = this.surgeReq; this.surgeReq = false; return b; }
  /** fire a skill (or fusion) now: cooldown reset + a short window in which it may cast. false = not owned / passive-only */
  triggerNow(id: string): boolean {
    const s = this.sim, p = s.player;
    if (isFusionId(id)) { const o = s.fusion.owned.get(id); if (!o) return false; o.t = 0; this.fireT[id] = 0.5; return true; }
    const w = id as WeaponId;
    if (!s.weapons.has(w)) return false;
    if (w === 'orbit') return true; // always on
    if (w === 'lance') { if (p.beamT > 0 || p.beamCharge > 0) return true; p.beamCd = 0; this.beamReq = true; this.fireT[w] = 0.5; return true; }
    if (w === 'surge') { if (p.transformT > 0) return true; s.surgeCdT = 0; p.ki = Math.max(p.ki, s.kiCap, SURGE_STAGE_END[0]); this.surgeReq = true; return true; }
    if (w === 'mortar' && s.mortar.charging) return true;
    s.wTimers[w] = 0; this.fireT[w] = 0.5; return true;
  }

  // ------------------------------------------------------------------ loadout
  /** skills in build slots (innate excluded) */
  private slotsFull(): boolean { return !this.noSlotLimit && this.sim.techSlotsUsed >= MAX_WEAPONS; }
  /** set a skill's rank (0 drops it). false when no slot is free. */
  setWeapon(id: WeaponId, rank: number): boolean {
    const s = this.sim; rank = Math.max(0, Math.min(MAX_WEAPON_LEVEL, Math.round(rank)));
    if (rank === 0) { s.dropWeapon(id); this.swapT = 0.35; return true; }
    if (!s.weapons.has(id) && !INNATE.includes(id) && this.slotsFull()) return false;
    s.weapons.set(id, rank);
    if (rank < MAX_WEAPON_LEVEL) s.evolved.delete(id);
    this.swapT = 0.35; return true;
  }
  /** evolve on / off. On: the skill goes to max rank (added if needed); the paired trait is not required in training. */
  setEvolved(id: WeaponId, on: boolean): boolean {
    const s = this.sim;
    if (!on) { s.evolved.delete(id); return true; }
    if (!this.setWeapon(id, MAX_WEAPON_LEVEL)) return false;
    s.evolved.add(id); return true;
  }
  /** set a trait's rank (0 removes it). false when no slot is free. */
  setPassive(id: PassiveId, rank: number): boolean {
    const s = this.sim, p = s.player, max = PASSIVES[id].max ?? MAX_PASSIVE_LEVEL;
    rank = Math.max(0, Math.min(max, Math.round(rank)));
    const old = s.plv(id);
    if (old === 0 && rank > 0 && !this.noSlotLimit && s.traitSlotsUsed >= MAX_PASSIVES) return false;
    if (rank === 0) s.passives.delete(id); else s.passives.set(id, rank);
    if (id === 'secondwind' && rank !== old) s.swUsed = 0; // Second Wind (group C): changing its rank here refills the saves, so it can be re-tested
    if (id === 'vitality') { const dh = (rank - old) * PASSIVE_K.vitality; p.maxHp = Math.max(1, p.maxHp + dh); p.hp = Math.max(1, Math.min(p.maxHp, p.hp + Math.max(0, dh))); }
    this.swapT = 0.35; return true;
  }
  /** both partners to max, then fuse at once (already owned = true) */
  fuseNow(id: FusionId): boolean {
    const s = this.sim, r = FUSIONS[id];
    if (s.fusion.has(id)) return true;
    const was = this.noSlotLimit; this.noSlotLimit = true; // the fusion frees the partners' slots straight away
    s.weapons.set(r.a, MAX_WEAPON_LEVEL); s.weapons.set(r.b, MAX_WEAPON_LEVEL);
    this.noSlotLimit = was;
    const ok = s.fuse(id); if (ok) this.swapT = 0.6; return ok;
  }
  unfuse(id: FusionId): void { this.sim.fusion.remove(id); if (id === 'prismnova' && !this.sim.weapons.has('ward')) this.sim.wardCharges = 0; }
  unfuseAll(): void { for (const id of [...this.sim.fusion.owned.keys()]) this.unfuse(id); }
  maxAll(): void { for (const [id] of this.sim.weapons) this.setWeapon(id, MAX_WEAPON_LEVEL); }
  clearAll(): void { for (const id of [...this.sim.weapons.keys()]) if (!INNATE.includes(id)) this.sim.dropWeapon(id); this.unfuseAll(); }
  clearTraits(): void { for (const id of [...this.sim.passives.keys()]) this.setPassive(id, 0); }
  /** apply a preset (replaces the build) */
  applyBuild(b: BuildPreset): void {
    const s = this.sim;
    for (const id of [...s.weapons.keys()]) s.dropWeapon(id);
    this.unfuseAll(); this.clearTraits();
    const was = this.noSlotLimit; this.noSlotLimit = true;
    for (const id of b.fusions) this.fuseNow(id);
    for (const [id, l] of b.weapons) this.setWeapon(id, l);
    for (const id of b.evolved) if (s.weapons.has(id)) s.evolved.add(id);
    for (const [id, l] of b.passives) this.setPassive(id, l);
    this.noSlotLimit = was; this.swapT = 0.5;
  }
  resetLoadout(): void { this.applyBuild(this.kit); }
  /** RESET ALL: loadout, enemies, dummies (back to the line of 5), Surge, HP, meter */
  resetAll(): void {
    const s = this.sim, p = s.player;
    this.resetLoadout(); this.clearEnemies(); this.clearDummies(); this.placeDummies('line5');
    p.ki = 0; p.transformT = 0; p.surgeTier = 0; s.surgeCdT = 0; s.surgeFloor = 0; s.kiStallT = 0; p.maxHp = this.baseMaxHp; p.hp = p.maxHp;
    this.resetMeter();
  }
  resetMeter(): void {
    const s = this.sim;
    for (const k of Object.keys(s.dmgBySrc)) delete s.dmgBySrc[k];
    for (const k of Object.keys(s.dmgSince)) delete s.dmgSince[k];
    for (const k of Object.keys(this.hitsBySrc)) delete this.hitsBySrc[k];
    this.snaps.length = 0; this.bench = null;
    for (const d of this.dummies.values()) { d.buckets.fill(0); d.hitB.fill(0); d.total = 0; d.hits = 0; d.last = 0; }
  }

  // ------------------------------------------------------------------ armory
  /** act on the pedestal the hero stands at (or pedestal i) */
  armory(act: 'toggle' | 'up' | 'down' | 'evolve' | 'max', i = this.near): boolean {
    const ped = this.pedestals[i]; if (!ped) return false;
    const s = this.sim;
    if (ped.kind === 'fusion') {
      const f = ped.id as FusionId;
      if (act === 'toggle' || act === 'max') { if (s.fusion.has(f)) { if (act === 'toggle') this.unfuse(f); return true; } return this.fuseNow(f); }
      return false;
    }
    if (ped.kind === 'trait') {
      const t = ped.id as PassiveId, l = s.plv(t), max = PASSIVES[t].max ?? MAX_PASSIVE_LEVEL;
      switch (act) {
        case 'toggle': return this.setPassive(t, l > 0 ? 0 : 1);
        case 'up': return this.setPassive(t, Math.min(max, l + 1));
        case 'down': return this.setPassive(t, l - 1);
        case 'max': return this.setPassive(t, max);
        default: return false;
      }
    }
    const w = ped.id as WeaponId, l = s.wlv(w);
    switch (act) {
      case 'toggle': return this.setWeapon(w, l > 0 ? 0 : 1);
      case 'up': return this.setWeapon(w, Math.min(MAX_WEAPON_LEVEL, l + 1));
      case 'down': return this.setWeapon(w, l - 1);
      case 'max': return this.setWeapon(w, MAX_WEAPON_LEVEL);
      case 'evolve': return this.setEvolved(w, !s.evolved.has(w));
    }
  }

  // ------------------------------------------------------------------ dummies
  placeDummy(x: number, z: number, type: DummyType = 'std', move: DummyMove = 'still', speed = DUMMY_SLOW): Enemy | null {
    const s = this.sim, def = DUMMY_DEFS[type];
    const e = s.spawnEnemy(def.kind, x, z, type === 'elite', 1, true); if (!e) return null;
    e.def = def; e.dummy = DUMMY_TYPE_CODE[type];
    e.maxHp = e.hp = def.hp; e.atkCd = 1e9; e.y = def.kind === 'boss' ? 2.6 : e.y;
    if (type === 'boss') s.attachDummyForms(e);
    this.dummies.set(e.id, { id: e.id, type, move, speed, ax: x, az: z, ph: 0, buckets: new Array(NB).fill(0), hitB: new Array(NB).fill(0), total: 0, hits: 0, last: 0 });
    return e;
  }
  /** a layout around the hero (dummies always stand in front of it: +z, the camera side) */
  placeDummies(layout: DummyLayout, type: DummyType = 'std', move: DummyMove = 'still', speed = DUMMY_SLOW): number {
    const p = this.sim.player; let n = 0;
    const put = (x: number, z: number) => { if (this.placeDummy(x, z, type, move, speed)) n++; };
    const fx = p.x, fz = p.z;
    switch (layout) {
      case 'one': put(fx, fz + 6); break;
      case 'line5': for (let k = 0; k < 5; k++) put(fx + (k - 2) * 3, fz + 8); break;
      case 'ring8': for (let k = 0; k < 8; k++) { const a = (k / 8) * Math.PI * 2; put(fx + Math.cos(a) * 7, fz + Math.sin(a) * 7); } break;
      case 'cluster20': for (let k = 0; k < 20; k++) { const a = k * 2.399963, r = 0.9 * Math.sqrt(k + 0.5) * 1.15; put(fx + Math.cos(a) * r, fz + 9 + Math.sin(a) * r); } break;
    }
    return n;
  }
  clearDummies(): void {
    for (const e of this.sim.enemies) if (e.alive && e.dummy) { e.alive = false; this.sim.forgetBoss(e); }
    this.dummies.clear();
  }
  /** Sim.updateEnemies hook (instead of the AI): rooted or on its path, never attacks, form beats for the boss dummy */
  updateDummy(e: Enemy): void {
    const di = this.dummies.get(e.id); e.flash = Math.max(0, e.flash - DT); e.phase += DT;
    e.kx = e.kz = 0; e.lift = 0; e.ky = 0; e.airT = 0; e.stunT = 0; e.pinT = 0;
    if (e.burnT > 0) { /* burn ticks still land (Sim.updateBurns) */ }
    if (e.form >= 0 && e.formT > 0) this.sim.stepBossForm(e);
    if (!di) return;
    let x = di.ax, z = di.az;
    if (di.move !== 'still') {
      di.ph += (di.speed / DUMMY_PATH_R) * DT;
      if (di.move === 'circle') { x += Math.cos(di.ph) * DUMMY_PATH_R; z += Math.sin(di.ph) * DUMMY_PATH_R; }
      else x += Math.sin(di.ph) * DUMMY_PATH_R;
    }
    e.vx = (x - e.x) / DT; e.vz = (z - e.z) / DT; e.x = x; e.z = z;
    // never dies: refill (the boss dummy cycles back to its first form after its last)
    if (e.hp < e.maxHp * DUMMY_REFILL) {
      if (e.form >= 0) { if (e.formT <= 0) { this.sim.resetBossForms(e); e.hp = e.maxHp; } } // after its last form (or a blow past every gate)
      else e.hp = e.maxHp;
    }
  }
  /** damage taken over the readout window (dps) and the hits in it */
  dummyDps(id: number): { dps: number; hits: number; total: number; last: number } {
    const d = this.dummies.get(id); if (!d) return { dps: 0, hits: 0, total: 0, last: 0 };
    let sum = 0, h = 0; for (let k = 0; k < NB; k++) { sum += d.buckets[k]; h += d.hitB[k]; }
    return { dps: sum / DUMMY_WINDOW, hits: h, total: d.total, last: d.last };
  }

  // ------------------------------------------------------------------ enemies / bosses
  /** spawn n of a family (null = mixed) in a ring 12–20 m around the hero */
  spawnFamily(kind: EnemyKind | null, n: number, elite = false): number {
    const s = this.sim, p = s.player, mix: EnemyKind[] = ['drone', 'skitter', 'brute', 'caster', 'lancer', 'stomper', 'bomber', 'splitter', 'wisp', 'warden'];
    const hp = s.enemyHpMul(s.schedTime); let c = 0;
    for (let k = 0; k < n; k++) {
      const a = s.rng.range(0, Math.PI * 2), d = s.rng.range(12, 20);
      const kd = kind ?? mix[k % mix.length];
      if (s.spawnEnemy(kd, p.x + Math.cos(a) * d, p.z + Math.sin(a) * d, elite, hp)) c++;
    }
    return c;
  }
  /** fill the field up to the enemy cap (stress test) */
  stress(target: number): number { const n = Math.max(0, Math.min(target, this.sim.opts.maxEnemies) - this.sim.aliveEnemies); return this.spawnFamily(null, n); }
  /** a guardian (0..2) or the map's final boss ('final'), straight into form 1..3 (forms 2 / 3 play the real transformation) */
  spawnBoss(which: 'final' | number, form: number): Enemy | null {
    const e = this.sim.trainSpawnBoss(which); if (!e) return null;
    if (form > 1) this.sim.forceBossForm(e, form);
    return e;
  }
  /** the living boss (or boss dummy if none) into form 1..3 */
  forceForm(form: number): boolean {
    const s = this.sim, e = s.boss && s.boss.alive ? s.boss : null; if (!e) return false;
    s.forceBossForm(e, form); return true;
  }
  clearEnemies(): void { this.sim.trainClearEnemies(); this.bossTimer = -1; }

  // ------------------------------------------------------------------ player / world
  fillSurge(): void { const s = this.sim; s.surgeCdT = 0; s.player.ki = s.kiCap; }
  /** Surge into form 1..SURGE_TIERS now. Forms I-III rank Surge up far enough (1 / 3 / 5); forms IV+ also grant whatever
   *  the run would need: rank 5 plus Ascendant Form (the evolution), and the run-progression gates (level, boss, Endless)
   *  are lifted by sim.surgeFloor, so INFINITE / UNLIMITED Surge then hold that form too */
  surgeTier(t: number): void {
    const s = this.sim, p = s.player; t = Math.max(1, Math.min(SURGE_TIERS, t));
    if (!s.weapons.has('surge')) s.weapons.set('surge', 1);
    const need = t === 1 ? 1 : t === 2 ? 3 : 5; if (s.wlv('surge') < need && !(t === 3 && s.evolved.has('surge'))) s.weapons.set('surge', need);
    if (t >= 4) { s.weapons.set('surge', 5); s.evolved.add('surge'); }
    s.surgeFloor = Math.max(s.surgeFloor, t);
    p.surgeTier = t; p.transformT = s.surgeDurationFor(t); p.ki = 0; s.surgeCdT = 0; s.kiStallT = 0;
    s.events.push({ t: 'transform', tier: t }); s.surgePush(p.x, p.z, t);
  }
  fillHp(): void { const p = this.sim.player; p.hp = p.maxHp; }
  setMaxHp(v: number): void { const p = this.sim.player; p.maxHp = Math.max(1, Math.round(v)); p.hp = Math.min(p.hp, p.maxHp); }
  levelUp(n = 1): void { const p = this.sim.player; this.forceLevel = true; for (let k = 0; k < n; k++) { this.sim.addXp(Math.max(0, p.xpNext - p.xp) + 1e-6); } this.forceLevel = false; }
  /** levelUp() is running: NO LEVEL-UPS does not block the explicit buttons */
  forceLevel = false;
  /** enemy HP multiplier: living foes (not dummies) rescale with it */
  setEnemyHp(k: number): void {
    const old = this.tw.ehp; this.tw.ehp = k;
    if (old > 0) for (const e of this.sim.enemies) if (e.alive && !e.dummy) { e.maxHp *= k / old; e.hp *= k / old; }
  }
  /** run modifier on / off mid-run (MOD_LIVE notes what each does live) */
  setMod(id: ModId, on: boolean): void {
    const s = this.sim, M = s.mods as Record<ModId, boolean | undefined>;
    const oldHp = s.hpK; M[id] = on || undefined;
    if (on) for (const c of MOD_CONFLICTS[id]) M[c] = undefined; // (Relaxed vs Tougher Foes / No Healing, as on Run Setup)
    if (id === 'hyper') s.setPace(on ? PACE_HYPER : PACE);
    if (id === 'tough' || id === 'relaxed') { // foe health / hits: Tougher Foes x Relaxed (difficulty presets)
      s.setToughness((M.tough ? TOUGH_HP : 1) * (M.relaxed ? RELAXED_HP : 1), (M.tough ? TOUGH_DMG : 1) * (M.relaxed ? RELAXED_DMG : 1));
      for (const e of s.enemies) if (e.alive && !e.dummy) { e.maxHp *= s.hpK / oldHp; e.hp *= s.hpK / oldHp; }
    }
    if (id === 'hurry') s.setSchedK(on ? 1 / HURRY_K : 1);
    if (id === 'night') this.clockOverride = on ? 0 : null;
  }
  /** start a map hazard now: the zone of that kind (and tag) nearest the hero starts its warning next tick */
  triggerHazard(kind: string, tag?: string): boolean {
    const s = this.sim, p = s.player; let best = null as null | (typeof s.hazards.zones)[number], bd = Infinity;
    for (const z of s.hazards.zones) {
      if (z.kind !== kind || (tag !== undefined && z.tag !== tag)) continue;
      if (z.phase === HZ.warn || z.phase === HZ.active) continue;
      const d = z.global ? 0 : (z.x - p.x) ** 2 + (z.z - p.z) ** 2; if (d < bd) { bd = d; best = z; }
    }
    if (!best) return false;
    best.phase = HZ.idle; best.next = s.time; best.t = 0;
    return true;
  }
  /** this map's hazard groups (kind + tag), for the UI */
  hazardGroups(): { kind: string; tag?: string; n: number }[] {
    const m = new Map<string, { kind: string; tag?: string; n: number }>();
    for (const z of this.sim.hazards.zones) { const k = z.kind + '|' + (z.tag ?? ''); const g = m.get(k); if (g) g.n++; else m.set(k, { kind: z.kind, tag: z.tag, n: 1 }); }
    return [...m.values()];
  }

  // ------------------------------------------------------------------ meter + benchmark
  /** damage per second per source over the last METER_SECS (or since the meter started) */
  meter(): { src: string; dps: number }[] {
    const s = this.sim, old = this.snaps[0]; const span = old ? Math.max(DT, s.time - old.t) : 0;
    if (!old || span <= 0) return [];
    const out: { src: string; dps: number }[] = [];
    for (const [k, v] of Object.entries(s.dmgBySrc)) { const d = v - (old.d[k] ?? 0); if (d > 0) out.push({ src: k, dps: d / span }); }
    return out.sort((a, b) => b.dps - a.dps);
  }
  startBench(): void { const s = this.sim; this.bench = { t: s.time, d0: { ...s.dmgBySrc }, h0: { ...this.hitsBySrc } }; }
  /** seconds left on the benchmark (0 = none running) */
  get benchLeft(): number { return this.bench ? Math.max(0, BENCH_SECS - (this.sim.time - this.bench.t)) : 0; }
  private finishBench(): void {
    const b = this.bench!; this.bench = null;
    this.benchPrev = this.benchResult;
    this.benchResult = benchTable(b.d0, this.sim.dmgBySrc, b.h0, this.hitsBySrc, BENCH_SECS);
  }

  /** jump the run clock (schedule follows: waves, elites, overtime, boss timing) */
  jumpClock(minutes: number): void { this.sim.jumpClock(minutes * 60); }
  /** boss in `secs` seconds (training timer) */
  bossIn(secs: number): void { this.bossTimer = Math.max(DT, secs); }
}

/** is a weapon id a real, offerable skill (UI lists) */
export const trainingSkills = (): WeaponId[] => (Object.keys(WEAPONS) as WeaponId[]).filter((id) => !WEAPONS[id].retired);
/** enemy families for the spawn buttons */
export const TRAIN_FAMILIES: readonly EnemyKind[] = (Object.keys(ENEMIES) as EnemyKind[]).filter((k) => k !== 'boss');
